"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import Icon from "@/components/icons";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
} from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import useIsMobile from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { BadgeIcon } from "@/components/ui/badgeIcon";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import sanitizeAndParseInlineHTML from "@/utils/functions/sanitizeAndParseInlineHTML";
import type { GeoPricingLocation } from "./types";
import useTranslations from "@/i18n/useTranslations";

type GeoPricingRibbonBannerProps = {
  badgeText?: string;
  label?: string;
  tooltip?: string;
  className?: string;
  location: GeoPricingLocation;
};

export default function GeoPricingRibbonBanner({
  badgeText,
  label,
  tooltip,
  className,
  location,
}: GeoPricingRibbonBannerProps) {
  const t = useTranslations("GeoPricing");

  const [isDismissed, setIsDismissed] = useState(false);
  const isMobile = useIsMobile();
  const hasViewedRef = useRef(false);
  const { ref: inViewRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  const ribbonWebElement = useMemo(
    () => ({
      elementType: "TEXT",
      htmlId: "geo-discount-ribbon",
      location,
      name: "geo-ribbon",
      text: label ?? "",
    }),
    [location, label],
  );

  const dismissWebElement = useMemo(
    () => ({
      elementType: ElementType.Button,
      htmlId: "mini-banner-geo-click",
      location,
      name: "mini-banner-geo-dismiss",
      text: "close",
    }),
    [location],
  );

  const { elementViewed } = useElementEvents({ webElement: ribbonWebElement });
  const { elementClicked: dismissClicked } = useElementEvents({
    webElement: dismissWebElement,
  });

  useEffect(() => {
    if (inView && !hasViewedRef.current) {
      hasViewedRef.current = true;
      elementViewed();
    }
  }, [inView, elementViewed]);

  const handleDismiss = useCallback(() => {
    dismissClicked();
    setIsDismissed(true);
  }, [dismissClicked]);

  if (isDismissed || (!badgeText && !label)) {
    return null;
  }

  return (
    <div
      ref={inViewRef}
      className={cn(
        "w-full bg-[var(--Brand-Elm-200,#BFC9CA)] py-3",
        isMobile ? "px-3" : "px-4",
        className,
      )}
      data-testid="geo-pricing-ribbon-banner"
      role="region"
      aria-label={t("bannerAriaLabel")}
    >
      <div className="mx-auto flex w-full max-w-screen-xl items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          {badgeText ? (
            <BadgeIcon
              icon="none"
              color="primaryLight"
              withBorder
              iconSize={14}
              className="shrink-0 whitespace-nowrap gap-2 md:border-primary-light md:bg-secondary-500 md:text-white"
            >
              <span className="text-micro">{badgeText}</span>
            </BadgeIcon>
          ) : null}
          {label ? (
            <div className="flex text-black">
              <p className="text-md m-0 text-inherit">{label}</p>

              {tooltip ? (
                <Popover>
                  <PopoverTrigger
                    aria-label={t("information")}
                    className="inline-flex translate-y-0.5 pl-1"
                  >
                    <Icon
                      type="info"
                      svgProps={{
                        width: 18,
                        height: 18,
                        className: "text-inherit hover:text-gray-700",
                      }}
                    />
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    side={isMobile ? "bottom" : "right"}
                    className="max-w-xs rounded-xl bg-primary text-sm text-primary-foreground"
                  >
                    {sanitizeAndParseInlineHTML(tooltip, true)}
                  </PopoverContent>
                </Popover>
              ) : null}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          aria-label={t("dismissBanner")}
          data-testid="geo-pricing-ribbon-banner-dismiss"
          onClick={handleDismiss}
        >
          <Icon
            type="close"
            svgProps={{
              width: 20,
              height: 20,
              "aria-hidden": true,
            }}
          />
        </button>
      </div>
    </div>
  );
}
